import React, { useEffect, useRef, useState } from 'react';

function shouldShowDebugOverlay() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === '1') return true;
    return window.localStorage && window.localStorage.getItem('debugOverlay') === '1';
  } catch (_) {
    return false;
  }
}

export default function DebugConsoleOverlay() {
  const [visible, setVisible] = useState(shouldShowDebugOverlay());
  const [expanded, setExpanded] = useState(true);
  const [logs, setLogs] = useState([]);
  const originalRef = useRef({});

  useEffect(() => {
    if (!visible) return;

    const addLog = (level, args) => {
      const time = new Date().toLocaleTimeString();
      const message = args.map((a) => {
        if (a instanceof Error) return `${a.name}: ${a.message}\n${a.stack || ''}`;
        if (typeof a === 'object') {
          try { return JSON.stringify(a); } catch (_) { return String(a); }
        }
        return String(a);
      }).join(' ');
      setLogs((prev) => {
        const next = [...prev, { level, time, message }];
        return next.slice(-200); // cap size
      });
    };

    const wrap = (level) => (...args) => {
      try { addLog(level, args); } catch (_) { }
      try { originalRef.current[level](...args); } catch (_) { }
    };

    originalRef.current = {
      log: console.log,
      warn: console.warn,
      error: console.error,
    };

    console.log = wrap('log');
    console.warn = wrap('warn');
    console.error = wrap('error');

    const onError = (event) => {
      addLog('error', [event?.message || 'Unhandled error', event?.error]);
    };
    const onRejection = (event) => {
      addLog('error', ['Unhandled promise rejection', event?.reason]);
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    return () => {
      // Restore originals
      if (originalRef.current.log) console.log = originalRef.current.log;
      if (originalRef.current.warn) console.warn = originalRef.current.warn;
      if (originalRef.current.error) console.error = originalRef.current.error;
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, [visible]);

  if (!visible) {
    return null;
  }

  const containerStyle = {
    position: 'fixed',
    left: 8,
    bottom: 8,
    zIndex: 99999,
    maxWidth: '90vw',
    maxHeight: '40vh',
    overflow: 'hidden',
    fontFamily: 'monospace',
    fontSize: 12,
    color: '#fff',
  };

  const panelStyle = {
    background: 'rgba(0,0,0,0.85)',
    border: '1px solid #333',
    borderRadius: 8,
    padding: 8,
    boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
    width: 'min(600px, 90vw)',
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  };

  const listStyle = {
    maxHeight: expanded ? '28vh' : 0,
    overflowY: 'auto',
    transition: 'max-height 0.2s ease-in-out',
  };

  const badge = (text, bg) => (
    <span style={{ background: bg, color: '#000', borderRadius: 4, padding: '2px 6px', marginRight: 6 }}>{text}</span>
  );

  return (
    <div style={containerStyle}>
      <div style={panelStyle}>
        <div style={headerStyle}>
          <div>
            {badge('DEBUG', '#FFD54F')}<span>Console</span>
          </div>
          <div>
            <button onClick={() => { try { window.location.href = 'http://octo.kevinvandertoorn.com/?debug=1'; } catch (_) { } }} style={{ marginRight: 6 }}>Production</button>
            <button onClick={() => setExpanded((v) => !v)} style={{ marginRight: 6 }}>Toggle</button>
            <button onClick={() => setLogs([])} style={{ marginRight: 6 }}>Clear</button>
            <button onClick={() => { setVisible(false); try { localStorage.setItem('debugOverlay', '0'); } catch (_) { } }}>Close</button>
          </div>
        </div>
        <div style={listStyle}>
          {logs.length === 0 && <div style={{ opacity: 0.7 }}>No logs yet…</div>}
          {logs.map((l, i) => (
            <div key={i} style={{ whiteSpace: 'pre-wrap', marginBottom: 6 }}>
              <span style={{ opacity: 0.6 }}>{l.time}</span>{' '}
              {l.level === 'error' ? badge('ERR', '#EF9A9A') : l.level === 'warn' ? badge('WARN', '#FFF59D') : badge('LOG', '#B3E5FC')}
              <span>{l.message}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 6, opacity: 0.7 }}>Tip: add ?debug=1 to the URL or set localStorage.debugOverlay = '1' to auto-show</div>
      </div>
    </div>
  );
}


